import { cn } from '@/helpers/utils/functionality-utils';
import { useTranslation } from '@/hooks/useTranslation';
import LanguageSelector from '@/modules/global-components/ui/lang_translation/LanguageSelector';
import { TenantInfo } from '@/modules/global-components/ui/TenantInfo';
import type { AppMenuItem, LayoutConfig } from '@/types';
import { Button } from 'primereact/button';
import { useContext, useState } from 'react';
import { TbShip } from 'react-icons/tb';
import { LayoutContext } from './context/context';
import { MenuProvider } from './context/menucontext';
import { useAppSelector } from '@/store/hooks';
import AppMenuitem from './AppMenuitem';

declare global {
  interface Window {
    APP_VERSION: string;
  }
}

const AppMenu = () => {
  const { t: translate } = useTranslation('common');
  const isCustomer = !!useAppSelector(
    (state) => state.user.authUserData.data.customer?.c_id,
  );

  const userPermissions = useAppSelector(
    (state) => state.user.authUserData.userPermissions,
  );

  const [scales] = useState([12, 13, 14, 15, 16]);
  const { layoutConfig, setLayoutConfig } = useContext(LayoutContext);

  const decrementScale = () => {
    setLayoutConfig((prevState: LayoutConfig) => ({
      ...prevState,
      scale: prevState.scale - 1,
    }));
  };

  const incrementScale = () => {
    setLayoutConfig((prevState: LayoutConfig) => ({
      ...prevState,
      scale: prevState.scale + 1,
    }));
  };

  const model: AppMenuItem[] = [
    {
      label: translate('Main'),
      items: [
        {
          label: translate('Dashboard'),
          icon: 'pi pi-home',
          to: '/main/dashboard',
        },
        {
          label: translate('Carriers'),
          icon: 'pi pi-car',
          to: '/main/carriers/list',
        },
        {
          label: translate('Customers'),
          icon: 'pi pi-users',
          to: '/main/customers/list',
        },
      ],
    },
    {
      label: translate('marketing'),
      items: [
        {
          label: translate('leads'),
          icon: 'pi pi-user-plus',
          to: '/main/marketing/leads',
        },
        {
          label: translate('showcase'),
          icon: 'pi pi-list',
          to: '/main/marketing/showcase',
        },
        {
          label: translate('reviews'),
          icon: 'pi pi-star',
          to: '/main/marketing/reviews',
        },
      ],
    },
    {
      label: translate('Export'),
      items: [
        {
          label: translate('Auctions'),
          icon: 'pi pi-money-bill',
          to: '/main/auction/list',
        },
        {
          label: translate('transports'),
          icon: 'pi pi-truck',
          to: '/main/transportation/list',
        },
        {
          label: translate('Inspection'),
          icon: 'pi pi-search-plus',
          to: '/main/inspection/list',
        },
        {
          label: translate('Shipping'),
          icon: <TbShip className="layout-menuitem-icon" />,
          to: '/main/shipping/list',
        },
      ],
    },
    {
      label: translate('Accounting'),
      items: [
        {
          label: translate('Invoices'),
          icon: 'pi pi-book',
          to: '/main/invoice/list',
        },
        {
          label: translate('Payments'),
          icon: 'pi pi-credit-card',
          to: '/main/payments/list',
        },
        {
          label: translate('Credit note'),
          icon: 'pi pi-wallet',
          to: '/main/credits/list',
        },
      ],
    },
    {
      label: translate('utilities'),
      items: [
        {
          label: translate('multiDocument'),
          icon: 'pi pi-book',
          to: '/main/multi-docs/list',
        },
        {
          label: translate('multiExpense'),
          icon: 'pi pi-credit-card',
          to: '/main/multi-expense',
        },
        {
          label: translate('dealerCarriers'),
          icon: 'pi pi-briefcase',
          to: '/main/dealer-uploads',
        },
      ],
    },
    {
      label: translate('Reports'),
      items: [
        // {
        //   label: translate('Custom report'),
        //   icon: 'pi pi-fw pi-bookmark',
        //   to: '/main/custom-report',
        // },
        {
          label: translate('taxBalanceReport'),
          icon: 'pi pi-arrow-right-arrow-left',
          to: '/main/urikake-maeuke',
        },
        {
          label: translate('Stock inventory'),
          icon: 'pi pi-exclamation-triangle',
          to: '/main/unsold-stock',
        },
        {
          label: translate('profitLoss'),
          icon: 'pi pi-chart-line',
          to: '/main/profit-loss',
        },
        {
          label: translate('Export ranking'),
          icon: 'pi pi-file-export',
          to: '/main/export-ranking',
        },
      ],
    },
    {
      label: translate('allMasters'),
      to: '/main/masters',
    },
  ];
  if (isCustomer) return;

  const utilitiesIndex = model.findIndex(
    (item) => item.label === translate('utilities'),
  );

  if (!userPermissions['multi_expense_menu']) {
    if (utilitiesIndex < 0) return;

    const items = model[utilitiesIndex]?.items;

    if (!items) return;

    const multiExpenseIndex = items.findIndex(
      (item) => item.label === translate('multiExpense'),
    );

    if (multiExpenseIndex < 0) return;

    items.splice(multiExpenseIndex, 1);
  }

  if (!userPermissions['multi_document_upload']) {
    if (utilitiesIndex < 0) return;

    const items = model[utilitiesIndex]?.items;

    if (!items) return;

    const multiDocumentIndex = items.findIndex(
      (item) => item.label === translate('multiDocument'),
    );

    if (multiDocumentIndex < 0) return;

    items.splice(multiDocumentIndex, 1);
  }

  return (
    <MenuProvider>
      <div className="rounded-md p-3 py-6 shadow sm:hidden">
        <TenantInfo containerClassName="w-fit sm:hidden" />
      </div>
      <div className="flex h-full flex-col justify-between gap-4">
        <ul className="layout-menu">
          {model.map((item, i) => {
            return !item?.seperator ? (
              <AppMenuitem item={item} root={true} index={i} key={item.label} />
            ) : (
              <li className="menu-separator"></li>
            );
          })}
        </ul>
        <ul>
          <li className="menu-item custom-language-selector mb-4 hidden">
            {/* keeping scale hidden for now */}
            <div className="p-2 text-sm"> {translate('Scale')}</div>
            <div className="flex items-center">
              <Button
                icon="pi pi-minus"
                type="button"
                onClick={decrementScale}
                rounded
                text
                className="w-20/12 mr-2 h-20"
                disabled={layoutConfig.scale === scales[0]}
              ></Button>
              <div className="flex items-center gap-2">
                {scales.map((item) => {
                  return (
                    <i
                      className={cn('pi pi-circle-fill', {
                        'text-primary-500': item === layoutConfig.scale,
                        'text-surface-300': item !== layoutConfig.scale,
                      })}
                      key={item}
                    ></i>
                  );
                })}
              </div>
              <Button
                icon="pi pi-plus"
                type="button"
                onClick={incrementScale}
                rounded
                text
                className="w-20/12 ml-2 h-20"
                disabled={layoutConfig.scale === scales[scales.length - 1]}
              ></Button>
            </div>
          </li>
          <li className="menu-item custom-language-selector">
            <div className="hidden">
              <div className="p-2">
                <span className="language-label text-sm">
                  {translate('Language')}
                </span>
              </div>
              <LanguageSelector />
            </div>
            <div className="border-t p-2">
              <span className="language-label flex items-center gap-2 text-xs">
                Version:<div>{'1.1'}</div>
              </span>
            </div>
          </li>
        </ul>
      </div>
    </MenuProvider>
  );
};

export default AppMenu;
